import React, { useRef, useState, useEffect } from "react";
import { RCCButton, RCCCheckBox, RCCGrid, RCCHidden } from "react-common-components";
import Accordion from "src/components/Accordion";
import Photo from "src/components/Photo";
import Title from "src/components/Title";
import { useTranslation } from "src/context/Translations/context";
import { IGrouperOfIndicators, IIndicatorsLevel } from "./interface";
import ListOfIndicators, { IRefListOfIndicators } from "./listOfIndicators";
import { IGroupersIndicators, IndicatorsGroupers } from "../interface";
import { useIndicatorsPanel } from "../indicatorsPanelContext";

const FilterButtonSelected = "padding: 2px 8px; filter: brightness(0.5);";
const FilterButton = "padding: 2px 8px;";

const GrouperOfIndicators: React.FC<IGrouperOfIndicators> = ({
	grouper,
	filters,
	groupersSelect,
	showValues,
	layoutList,
	expandedProp,
	onCallbackChangeValuesIndicator,
	recursiveProps
}) => {
	const { translate } = useTranslation();
	const { showCheckboxes } = useIndicatorsPanel();
	const { REACT_APP_S3_ADDRESS, REACT_APP_S3_DIRECTORY } = process.env;

	const [internalExpanded, setInternalExpanded] = useState(false);

	const listOfIndicatorsRef = useRef<IRefListOfIndicators>(null);

	const [checkboxDisabled, setCheckboxDisabled] = useState(true);
	const [checkAll, setCheckAll] = useState(recursiveProps?.checkAll ?? false);

	const [grouperData, setGrouperData] = useState<IGroupersIndicators>(grouper);
	const [filterList, setFilterList] = useState<IIndicatorsLevel | undefined>(recursiveProps?.initialFilterList);
	const [executeQuery, setExecuteQuery] = useState<boolean>(false);

	const [allCollapsedExceptIndex, setAllCollapsedExceptIndex] = useState<number | undefined>(undefined);
	const executeCollapseAllExceptIndex = (index?: number) => {
		setAllCollapsedExceptIndex(index);
	};

	const filterIndicatorsOnClickCountBar = (situation: IIndicatorsLevel) => {
		let situationToUpdate: IIndicatorsLevel | undefined = situation;
		
		if(filterList && filterList === situation)
			situationToUpdate = undefined;
		
		setFilterList(situationToUpdate);
	};

	const handleClickGroupers = async (_e: any, expanded: boolean) => {
		if(expandedProp !== undefined && onCallbackChangeValuesIndicator)
			onCallbackChangeValuesIndicator(true);
		setInternalExpanded(expanded);
		if(!expanded){
			setCheckboxDisabled(true);
			return;
		}
		setExecuteQuery(true);
		setCheckboxDisabled(false);
	};

	const onCallbackDelete = (deletedLevel:IIndicatorsLevel, updatedGrouperData?: IGroupersIndicators) => {
		let updateChildrenGrouper: IGroupersIndicators | undefined;
		setGrouperData((prevState) => {
			const updateState = {...prevState};
			updateState.count = prevState.count - 1;
			updateState.operational = deletedLevel === IIndicatorsLevel.OPERATIONAL ? prevState.operational - 1 : prevState.operational;
			updateState.strategic = deletedLevel === IIndicatorsLevel.STRATEGIC ? prevState.strategic - 1 : prevState.strategic;
			updateState.tactic = deletedLevel === IIndicatorsLevel.TACTIC ? prevState.tactic - 1 : prevState.tactic;
			if(updatedGrouperData && updateState.groupersChildren && updateState.groupersChildren !== null){
				updateState.groupersChildren = updateState.groupersChildren.map((groupChildren) => {
					if(groupChildren.id === updatedGrouperData.id && groupChildren.id2 === updatedGrouperData.id2 && groupChildren.id3 === updatedGrouperData.id3)
						return updatedGrouperData;
					return groupChildren;
				});
			}
			updateChildrenGrouper = updateState;
			return {
				...updateState
			};
		});
		if(recursiveProps?.onCallbackDeleteParent)
			recursiveProps.onCallbackDeleteParent(deletedLevel, updateChildrenGrouper);
	};

	const onCallbackUpdate = (newLevel:IIndicatorsLevel, oldLevel:IIndicatorsLevel, updatedGrouperData?: IGroupersIndicators) => {
		let updateChildrenGrouper: IGroupersIndicators | undefined;
		setGrouperData((prevState) => {
			const updateState = {...prevState};
			
			switch (newLevel) {
			case IIndicatorsLevel.OPERATIONAL:
				updateState.operational +=1;
				break;
			case IIndicatorsLevel.STRATEGIC:
				updateState.strategic +=1;
				break;
			default:
				updateState.tactic +=1;
				break;
			}

			switch (oldLevel) {
			case IIndicatorsLevel.OPERATIONAL:
				updateState.operational -=1;
				break;
			case IIndicatorsLevel.STRATEGIC:
				updateState.strategic -=1;
				break;
			default:
				updateState.tactic -=1;
				break;
			}	

			if(updatedGrouperData && updateState.groupersChildren && updateState.groupersChildren !== null){
				updateState.groupersChildren = updateState.groupersChildren.map((groupChildren) => {
					if(groupChildren.id === updatedGrouperData.id && groupChildren.id2 === updatedGrouperData.id2 && groupChildren.id3 === updatedGrouperData.id3)
						return updatedGrouperData;
					return groupChildren;
				});
			}
			updateChildrenGrouper = updateState;

			return{
				...updateState
			};
		});
		if(recursiveProps?.onCallbackUpdateParent)
			recursiveProps.onCallbackUpdateParent(newLevel, oldLevel, updateChildrenGrouper);
	};

	useEffect(() => setInternalExpanded((prevState) => expandedProp !== undefined ? expandedProp : prevState), [expandedProp]);

	useEffect(() => {
		if(!executeQuery)
			return;
		listOfIndicatorsRef.current?.executeQuery();
		setExecuteQuery(false);
	}, [executeQuery]);

	useEffect(() => {
		if(recursiveProps)
			setFilterList(recursiveProps.initialFilterList);
	}, [recursiveProps?.initialFilterList]);

	useEffect(() => {
		if(recursiveProps)
			setCheckAll(recursiveProps.checkAll);
	}, [recursiveProps?.checkAll]);

	return (
		<>
			<Accordion 
				header={
					<RCCGrid 
						container
						item 
						alignItems="center"
						justifyContent="space-between"
					>
						<span style={{ display: "flex", alignItems: "center"}}>
							{!(grouperData.groupersChildren && grouperData.groupersChildren !== null && grouperData.groupersChildren.length > 0) && showCheckboxes && (
								<RCCCheckBox
									name={`selectAllIndicator-${grouperData.id}-${grouperData.id2}-${grouperData.id3}`}
									onChange={() => setCheckAll((prevstate) => !prevstate)}
									checked={checkAll}
									disabled={checkboxDisabled}
									useStopPropagation
									styleInline={{ checkbox: "margin: 0; margin-right: 2px; margin-left: -0.8rem;" }}
								/>
							)}
							{grouperData.image !== null && (
								<Photo 
									hover
									name={grouperData.imageName || ""}
									link={grouperData.image !== "" ? `${REACT_APP_S3_ADDRESS}/${REACT_APP_S3_DIRECTORY}/imagens_usuarios/${grouperData.image}` : ""}
								/>
							)}
							{grouperData.title !== null && translate(`indicatorWorkspace:${grouperData.title}`)}
							{grouperData.title2 !== null && translate(`indicatorWorkspace:${grouperData.title2}`)}
							{grouperData.title3 !== null && translate(`indicatorWorkspace:${grouperData.title3}`)}
						</span>
						<RCCGrid container justifyContent="flex-end">
							{groupersSelect.group !== IndicatorsGroupers.INDICATOR_HIERARCHY && (
								<>
									{grouperData.strategic > 0 && (
										<RCCButton 
											color="purple" 
											styleInline={filterList && filterList !== IIndicatorsLevel.STRATEGIC ? FilterButtonSelected : FilterButton}
											onClick={(e) => { e.stopPropagation(); filterIndicatorsOnClickCountBar(IIndicatorsLevel.STRATEGIC); }}
										>
											{grouperData.strategic} <RCCHidden only="xs">{translate("estratégico(s)")}</RCCHidden>
										</RCCButton>
									)}
									{grouperData.tactic > 0 && (
										<RCCButton 
											color="blue" 
											styleInline={filterList && filterList !== IIndicatorsLevel.TACTIC ? FilterButtonSelected : FilterButton}
											onClick={(e) => { e.stopPropagation(); filterIndicatorsOnClickCountBar(IIndicatorsLevel.TACTIC); }}
										>
											{grouperData.tactic} <RCCHidden only="xs">{translate("tático(s)")}</RCCHidden>
										</RCCButton>
									)}
									{grouperData.operational > 0 && (
										<RCCButton 
											color="orange" 
											styleInline={filterList && filterList !== IIndicatorsLevel.OPERATIONAL ? FilterButtonSelected : FilterButton}
											onClick={(e) => { e.stopPropagation(); filterIndicatorsOnClickCountBar(IIndicatorsLevel.OPERATIONAL); }}
										>
											{grouperData.operational} <RCCHidden only="xs">{translate("operacional(s)")}</RCCHidden>
										</RCCButton>
									)}
								</>
							)}
							<Title color="purple" size="sm" styleInline="margin 0 4px;">{translate("Total")}: {grouperData.count}</Title>
						</RCCGrid>
					</RCCGrid>
				}
				expanded={internalExpanded}
				onChange={(e, expanded) => handleClickGroupers(e, expanded)}
			>		
				{grouperData.groupersChildren && grouperData.groupersChildren !== null && grouperData.groupersChildren.length > 0 ? grouperData.groupersChildren.map((grouperChildren, index: number) => (
					<GrouperOfIndicators 
						key={`groupersChildren-${grouperChildren.id}-${grouperChildren.id2}-${grouperChildren.id3}`}
						filters={filters}
						grouper={grouperChildren}
						groupersSelect={groupersSelect}
						layoutList={layoutList}
						showValues={showValues}
						expandedProp={allCollapsedExceptIndex === undefined ? undefined : allCollapsedExceptIndex === index}
						onCallbackChangeValuesIndicator={(reset: boolean) => {
							if(onCallbackChangeValuesIndicator)
								onCallbackChangeValuesIndicator(reset);
							executeCollapseAllExceptIndex(reset ? undefined : index);
						}}
						recursiveProps={{
							onCallbackDeleteParent: onCallbackDelete,
							onCallbackUpdateParent: onCallbackUpdate,
							initialFilterList: filterList,
							checkAll
						}}
					/>
				)) : (
					<ListOfIndicators 
						ref={listOfIndicatorsRef}
						groupers={{
							grouperId: grouper.id, 
							grouperId2: grouper.id2, 
							grouperId3: grouper.id3
						}}
						filters={filters}
						groupersSelect={groupersSelect}
						showValues={showValues}
						onCallbackDelete={onCallbackDelete}
						onCallbackUpdate={onCallbackUpdate}
						layoutList={layoutList}
						filterList={filterList}
						onCallbackChangeValuesIndicator={onCallbackChangeValuesIndicator}
						defaultChecked={checkAll}
					/>	          
				)}
				
			</Accordion>
		
		</>
	);
};

export default GrouperOfIndicators;
